import {Dialog} from '@base-ui/react/dialog';
import {CloseIcon} from '@uwm/ui';
import cn from 'clsx';
import type {ReactNode} from 'react';

import useExceedsViewportHeight from '@/hooks/useExceedsViewportHeight';
import useHideMenu from '@/hooks/useHideMenu';

const getRootElement = () => document.getElementById('root');

interface DrawerProps {
    open: boolean;
    onClose: () => void;
    closeLabel?: string;
    className?: string;
    children: ReactNode;
    'aria-labelledby'?: string;
}

const Drawer = ({open, onClose, closeLabel, className, children, 'aria-labelledby': ariaLabelledBy}: DrawerProps) => {
    // Full height once the popup would reach 90% of the viewport.
    const [popupRef, bodyRef, isFullHeight] = useExceedsViewportHeight(0.9);
    const isMenuHidden = useHideMenu(open);

    return (
        <Dialog.Root open={open && isMenuHidden} onOpenChange={(isOpen) => !isOpen && onClose()}>
            {/* state/ducks/uwm.ts toggles .dark on #root, so a portal to document.body would drop every dark: variant. */}
            <Dialog.Portal container={getRootElement()}>
                <Dialog.Backdrop className='fixed inset-0 z-100 animate-fade bg-overlay' />
                <Dialog.Popup
                    ref={popupRef}
                    aria-labelledby={ariaLabelledBy}
                    className={cn(
                        'fixed inset-x-0 bottom-0 z-101 flex max-h-dvh animate-slide-from-bottom flex-col gap-8 overflow-y-auto bg-secondary px-6 text-primary dark:bg-orion-2 dark:text-romance',
                        isFullHeight ? 'h-dvh rounded-none pt-inset-4' : 'rounded-t-2xl pt-8',
                        className
                    )}
                >
                    <Dialog.Close
                        aria-label={closeLabel}
                        className='flex size-11 shrink-0 cursor-pointer items-center justify-center self-end rounded-full bg-light-silver dark:bg-orion-card'
                    >
                        <CloseIcon />
                    </Dialog.Close>
                    {/* Only the body scrolls, so the close button stays reachable - its own padding keeps the last item off the edge. */}
                    <div ref={bodyRef} className='flex min-h-0 flex-col gap-8 overflow-y-auto pb-8'>
                        {children}
                    </div>
                </Dialog.Popup>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export default Drawer;
